import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser, signIn as amplifySignIn, signUp as amplifySignUp, confirmSignUp as amplifyConfirmSignUp, signOut as amplifySignOut, resendSignUpCode as amplifyResendSignUpCode, fetchAuthSession } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { User, AuthContextType } from '../types/auth';
import { googleAuthService } from '../services/googleAuth';

// Amplify is configured once in aws-config.ts and imported in App.tsx

// Create AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(false);

  useEffect(() => {
    console.log('🔍 AuthProvider: Component mounted, checking auth state...');
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      (async () => {
        try {
          setLoading(true);
          console.log('🔐 Handling Google OAuth callback');
          const tokens = await googleAuthService.handleCallback(code);
          const userInfo = tokens.userInfo;
          const currentUser: User = {
            username: userInfo.email,
            userId: userInfo.id,
            attributes: {
              sub: userInfo.id,
              email: userInfo.email,
              name: userInfo.name
            }
          };
          setUser(currentUser);
          console.log('✅ Google OAuth callback successful, user set');
        } catch (e: any) {
          console.log('❌ Google OAuth callback failed:', e?.message || e);
          setUser(null);
        } finally {
          setLoading(false);
          // Clean query params
          const url = new URL(window.location.href);
          url.searchParams.delete('code');
          url.searchParams.delete('state');
          window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
        }
      })();
      return;
    }
    checkAuthState();
  }, []);

  // Re-check auth when tab gains focus or becomes visible (handles idle/return scenarios)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Visibility change to visible → re-checking auth');
        checkAuthState();
      }
    };

    const handleWindowFocus = () => {
      console.log('🔄 Window focus → re-checking auth');
      checkAuthState();
    };

    const handleStorage = (e: StorageEvent) => {
      // If auth-related storage keys change in another tab, refresh auth
      if (
        e.key === 'google_oauth_token' ||
        (e.key && e.key.startsWith('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr'))
      ) {
        console.log('🔄 Storage change detected → re-checking auth');
        checkAuthState();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const checkAuthState = async () => {
    // Prevent multiple concurrent auth checks
    if (isCheckingAuth) {
      console.log('🔍 Auth check already in progress, skipping...');
      return;
    }
    
    try {
      setIsCheckingAuth(true);
      setLoading(true);
      setError(null);
      
      console.log('🔍 Checking auth state...');
      
      // Clean up expired Google token proactively
      try {
        const googleToken = localStorage.getItem('google_oauth_token');
        if (googleToken) {
          const payload: any = jwtDecode(googleToken);
          if (payload?.exp && payload.exp * 1000 <= Date.now()) {
            console.log('🧹 Removing expired Google token from storage');
            localStorage.removeItem('google_oauth_token');
          }
        }
      } catch {}

      // Prefer session tokens (Amplify) to derive authoritative user info
      try {
        console.log('🔍 Trying fetchAuthSession()...');
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const accessToken = session.tokens?.accessToken?.toString();
        const jwt = idToken || accessToken || null;
        if (jwt) {
          const payload = JSON.parse(atob(jwt.split('.')[1]));
          const currentUser: User = {
            username: payload.email || payload['cognito:username'] || payload.sub,
            userId: payload.sub,
            attributes: {
              sub: payload.sub,
              email: payload.email || undefined,
              name: payload.name || payload.email || payload['cognito:username'] || payload.sub
            }
          };
          console.log('✅ Derived user from fetchAuthSession:', currentUser);
          setUser(currentUser);
          return;
        }
      } catch (e: any) {
        console.log('ℹ️ fetchAuthSession failed:', e?.message || e);
      }

      // Next, check for Google OAuth authentication using the new service
      if (googleAuthService.isAuthenticated()) {
        try {
          const userInfo = googleAuthService.getCurrentUser();
          if (userInfo) {
            const currentUser: User = {
              username: userInfo.email,
              userId: userInfo.id,
              attributes: {
                sub: userInfo.id,
                email: userInfo.email,
                name: userInfo.name
              }
            };
            console.log('✅ Found Google user from auth service:', currentUser);
            setUser(currentUser);
            return;
          }
        } catch (tokenError: any) {
          console.log('❌ Google auth service check failed:', tokenError.message);
          googleAuthService.clearTokens();
        }
      }

      // Fallback: manual parse of Cognito idToken from localStorage
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
      if (lastAuthUser) {
        const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
        const idToken = localStorage.getItem(idTokenKey);
        if (idToken) {
          try {
            const tokenParts = idToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const currentUser: User = {
                username: payload.cognito_username || payload.sub,
                userId: payload.sub,
                attributes: {
                  sub: payload.sub,
                  email: payload.email,
                  name: payload.name
                }
              };
              console.log('✅ Created user from localStorage idToken:', currentUser);
              setUser(currentUser);
              return;
            }
          } catch (tokenError: any) {
            console.log('❌ Token parsing failed:', tokenError.message);
          }
        }
      }

      // Last resort: getCurrentUser (structure varies in Amplify v6)
      try {
        console.log('🔍 Trying getCurrentUser() as last resort...');
        const currentUser = await getCurrentUser();
        console.log('✅ getCurrentUser() result:', currentUser);
        setUser(currentUser as User);
      } catch (getUserError: any) {
        console.log('❌ getCurrentUser() failed:', getUserError.message);
        setUser(null);
      }
      
    } catch (err: any) {
      console.log('ℹ️ Auth check error:', err.message);
      setUser(null);
    } finally {
      console.log('🔍 Auth check completed, setting loading to false');
      setLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Attempting sign in...');
      
      // Check if user is already signed in
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          console.log('✅ User already signed in:', currentUser);
          setUser(currentUser as User);
          return { success: true, user: currentUser as User };
        }
      } catch (e) {
        // User not signed in, continue with sign in
      }
      
      const user = await amplifySignIn({
        username: email,
        password: password
      });
      
      console.log('✅ Sign in successful:', user);
      
      // Get the actual user ID from the JWT token
      let actualUserId = email; // fallback
      try {
        const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
        if (lastAuthUser) {
          const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
          const idToken = localStorage.getItem(idTokenKey);
          
          if (idToken) {
            const tokenParts = idToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              actualUserId = payload.sub;
              console.log('✅ Found actual user ID from token:', actualUserId);
            }
          }
        }
      } catch (tokenError) {
        console.log('❌ Failed to parse token for user ID:', tokenError);
      }
      
      // Create user object with the expected structure
      const userObj: User = {
        username: email,
        userId: actualUserId,
        attributes: {
          sub: actualUserId,
          email: email,
          name: email
        }
      };
      
      setUser(userObj);
      return { success: true, user: userObj };
      
    } catch (err: any) {
      console.log('❌ Sign in error:', err);
      
      if (err.message && err.message.includes('already a signed in user')) {
        console.log('ℹ️ User already signed in, getting current user...');
        try {
          const currentUser = await getCurrentUser();
          console.log('✅ Got current user:', currentUser);
          setUser(currentUser as User);
          return { success: true, user: currentUser as User };
        } catch (getUserError: any) {
          console.log('❌ Failed to get current user:', getUserError.message);
        }
      }
      
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Attempting sign up...');
      
      const result = await amplifySignUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            name: name
          }
        }
      });
      
      console.log('✅ Sign up successful:', result);
      return { success: true };
      
    } catch (err: any) {
      console.log('❌ Sign up error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const confirmSignUp = async (email: string, code: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Attempting to confirm sign up...');
      
      await amplifyConfirmSignUp({
        username: email,
        confirmationCode: code
      });
      
      console.log('✅ Sign up confirmation successful');
      return { success: true };
      
    } catch (err: any) {
      console.log('❌ Sign up confirmation error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const resendConfirmationCode = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Attempting to resend confirmation code...');
      
      await amplifyResendSignUpCode({
        username: email
      });
      
      console.log('✅ Resend confirmation code successful');
      return { success: true };
      
    } catch (err: any) {
      console.log('❌ Resend confirmation code error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (googleToken: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Attempting Google sign in...');
      
      // Use the new Google Auth service to handle the token
      const userInfo = await googleAuthService.handleIdToken(googleToken);
      
      // Create user object from Google token
      const userObj: User = {
        username: userInfo.email,
        userId: userInfo.id,
        attributes: {
          sub: userInfo.id,
          email: userInfo.email,
          name: userInfo.name
        }
      };
      
      console.log('✅ Google sign in successful:', userObj);
      setUser(userObj);
      return { success: true, user: userObj };
      
    } catch (err: any) {
      console.log('❌ Google sign in error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Attempting to sign out...');
      
      // Clear Google OAuth tokens using the auth service
      googleAuthService.clearTokens();
      
      // Try to sign out from Amplify (for Cognito users)
      try {
        await amplifySignOut();
      } catch (amplifyError) {
        console.log('ℹ️ Amplify sign out failed (user might be Google OAuth):', amplifyError);
      }
      
      setUser(null);
      
      console.log('✅ Sign out successful');
      
    } catch (err: any) {
      console.log('❌ Sign out error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserId = (): string | null => {
    console.log('🔍 getUserId - User:', user);
    console.log('🔍 getUserId - User attributes:', user?.attributes);
    console.log('🔍 getUserId - User username:', user?.username);
    console.log('🔍 getUserId - User userId:', user?.userId);
    console.log('🔍 getUserId - User isSignedIn:', user?.isSignedIn);
    
    if (!user) {
      console.log('❌ getUserId - No user found');
      return null;
    }
    
    try {
      // Synchronously attempt to read id from localStorage idToken first (stable across reloads)
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
      if (lastAuthUser) {
        const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
        const idToken = localStorage.getItem(idTokenKey);
        if (idToken) {
          const tokenParts = idToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('✅ getUserId - Found user ID from idToken:', payload.sub);
            return payload.sub;
          }
        }
      }
    } catch (e) {
      console.log('ℹ️ getUserId - localStorage idToken parse failed:', e);
    }

    // Fallbacks: Google token -> existing user state
    try {
      const googleToken = localStorage.getItem('google_oauth_token');
      if (googleToken) {
        const payload = jwtDecode(googleToken) as any;
        if (payload?.sub) {
          return payload.sub;
        }
      }
    } catch {}

    const userId = user?.attributes?.sub || user?.userId || user?.username;
    
    console.log('✅ getUserId - Returning user ID:', userId);
    return userId || null;
  };

  const getUserEmail = (): string | null => {
    if (user?.attributes?.email) {
      return user.attributes.email;
    }
    
    // Handle Amplify v6 format
    if (user?.isSignedIn && !user?.attributes) {
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
      if (lastAuthUser) {
        const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
        const idToken = localStorage.getItem(idTokenKey);
        
        if (idToken) {
          try {
            const tokenParts = idToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              return payload.email;
            }
          } catch (tokenError) {
            console.log('❌ Token parsing failed:', tokenError);
          }
        }
      }
    }
    
    return user?.username || null;
  };

  const getUserName = (): string | null => {
    if (user?.attributes?.name) {
      return user.attributes.name;
    }
    
    // Handle Amplify v6 format
    if (user?.isSignedIn && !user?.attributes) {
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
      if (lastAuthUser) {
        const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
        const idToken = localStorage.getItem(idTokenKey);
        
        if (idToken) {
          try {
            const tokenParts = idToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              return payload.name || payload.email;
            }
          } catch (tokenError) {
            console.log('❌ Token parsing failed:', tokenError);
          }
        }
      }
    }
    
    return user?.username || null;
  };

  const resetAuthState = () => {
    console.log('🔄 Resetting auth state...');
    setUser(null);
    setLoading(false);
    setIsCheckingAuth(false);
    setError(null);
  };

  const manualTokenParse = async () => {
    console.log('🔍 Manual token parsing...');
    
    try {
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
      if (lastAuthUser) {
        const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
        const idToken = localStorage.getItem(idTokenKey);
        
        if (idToken) {
          const tokenParts = idToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('🔍 Manual token payload:', payload);
            
            const currentUser: User = {
              username: payload.cognito_username || payload.sub,
              userId: payload.sub,
              attributes: {
                sub: payload.sub,
                email: payload.email,
                name: payload.name
              }
            };
            console.log('✅ Manual token parsing successful:', currentUser);
            setUser(currentUser);
            setLoading(false);
            setIsCheckingAuth(false);
            return;
          }
        }
      }
      
      console.log('❌ No tokens found for manual parsing');
    } catch (error) {
      console.log('❌ Manual token parsing failed:', error);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    confirmSignUp,
    resendConfirmationCode,
    getUserId,
    getUserEmail,
    getUserName,
    checkAuthState,
    resetAuthState,
    manualTokenParse
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};